// ── Freigabe-Agenda — portfolio-weite Haltefrist-Übersicht (DE) ──────────────
//
// Aggregiert die OFFENEN FIFO-Lots ALLER Coins zu einer chronologischen Liste:
// „wann überschreitet welche Tranche die deutsche 1-Jahres-Frist?". Das ist die
// Portfolio-Ebene über dem per-Coin Smart Check (SmartCheckScreen / SmartCheckMini).
//
// AUDIT-EINORDNUNG (read-only Aggregations-Layer, KEINE zweite Engine):
//   • Mengen kommen AUSSCHLIESSLICH aus buildTaxLots → buildFifoPool (fifo.js),
//     der dokumentierten Single Source of Truth, die auch den angezeigten
//     Bestand speist. Damit gilt INVARIANT 5 (Σ offene Lots == Bestand) by
//     construction — hier wird NICHTS neu gerechnet. Vgl. taxLots.js-Historie
//     (fix_taxlots_parallel_fifo_divergence): keine parallele FIFO mehr.
//   • Frist-Datum kommt AUSSCHLIESSLICH aus holdingPeriod.js (Jahrestag + 1 Tag,
//     BGB §188 II) — identisch zur Engine + Alerts, NICHT „rollend 365". Damit
//     ist INVARIANT 6 (Lot > Frist ist nie steuerpflichtig) anzeige-konform:
//     bereits freie Lots erscheinen NICHT als kommendes Ereignis.
//   • €-Werte sind Display-Näherungen (menge × Live-Preis), exakt im Muster von
//     SmartCheckMini.js / TaxScreen.js. Bewusst Number wie der gesamte
//     Display-Layer — keine zweite Arithmetik einführen. Die finanzrelevanten
//     Mengen sind bereits in der Engine festgelegt; hier nur Multiplikation für
//     eine klar mit „≈" markierte Wertanzeige.
//
// Reine Daten-/ES-Module-Datei — keine React/Firebase-Deps (testbar wie taxLots.js).

import { buildTaxLots } from './taxLots';
import { isLotFree, lotFreeDate, lotFreeMs, daysUntilFree } from './holdingPeriod';
import { COIN_MAP } from './coingecko';
import { PORTFOLIO_FIAT_EXCLUDE } from './portfolioCalc';

// Sentinel für „Alle Coins" im Coin-Picker (Überblick-Modus statt Einzel-Coin).
export const ALL_COINS = '__ALL__';

/**
 * Coins mit offenen Lots, nach Bestandsgröße sortiert — die EINE Quelle für die
 * Coin-Liste in Smart Check (Screen + Mini). Vorher 1:1 dupliziert in TaxScreen
 * und SmartCheckMini; hier zentralisiert.
 *
 * Filter: Fiat raus (PORTFOLIO_FIAT_EXCLUDE), untrackbare Coins ohne COIN_MAP-ID
 * raus, Dust < ~1 € Marktwert raus. Coins ohne bekannten Preis bleiben sichtbar
 * (Wert unbestimmbar → nicht verstecken).
 *
 * @param {Array} txs           Roh-TX (nur zum Symbol-Sammeln).
 * @param {Array} dustFreeTxs   Scam-Dust-bereinigte TX (dropScamDustTxs) → FIFO.
 * @param {Object} marketData   { [coinId]: { price } }.
 * @returns {string[]}          Symbole (UPPERCASE), größter Bestand zuerst.
 */
export function availableHoldingCoins(txs, dustFreeTxs, marketData) {
  const set = new Set();
  for (const tx of txs || []) {
    const cand = [tx.coin, tx.coinIn, tx.coinOut].filter(Boolean);
    for (const c of cand) set.add(String(c).toUpperCase());
  }
  return [...set]
    .map(sym => ({ sym, lots: buildTaxLots(sym, dustFreeTxs) }))
    .filter(x => x.lots.length > 0)
    .filter(x => {
      if (PORTFOLIO_FIAT_EXCLUDE.has(x.sym)) return false;
      const id = COIN_MAP[x.sym]?.id;
      if (!id) return false;
      const price = marketData?.[id]?.price;
      if (!(typeof price === 'number' && price > 0)) return true; // Preis unbekannt → behalten
      const total = x.lots.reduce((s, l) => s + l.amount, 0);
      return total * price >= 1;
    })
    .sort((a, b) => {
      const ta = a.lots.reduce((s, l) => s + l.amount, 0);
      const tb = b.lots.reduce((s, l) => s + l.amount, 0);
      return tb - ta;
    })
    .map(x => x.sym);
}

/**
 * Baut die Freigabe-Agenda über die gegebenen Coins.
 *
 * @param {Object}  args
 * @param {string[]} args.coins        Symbole (z.B. aus availableHoldingCoins).
 * @param {Array}   args.dustFreeTxs   Scam-Dust-bereinigte TX → buildTaxLots.
 * @param {Object}  args.marketData    { [coinId]: { price } } für €-Werte.
 * @param {Date}    [args.today]       Stichtag (Default: jetzt).
 * @returns {{
 *   freeValueEur:number, boundValueEur:number, totalValueEur:number,
 *   freePct:(number|null), hasPricedHoldings:boolean,
 *   events:Array, nextEvent:(Object|null), eventCount:number
 * }}
 *   events: [{ sym, coinId, amount, buyDate, freeDate, freeMs, days, valueEur, hasPrice }]
 *   — nur NOCH GEBUNDENE Lots (kommende Freigaben), je (Coin, Frei-Datum)
 *   verdichtet, chronologisch sortiert (freeMs asc, dann sym asc → deterministisch).
 *   freePct ist wert-gewichtet über die bepreisten Coins (€ frei / € gesamt);
 *   null, wenn kein Coin einen Preis hat.
 */
export function buildReleaseAgenda({ coins, dustFreeTxs, marketData, today = new Date() }) {
  let freeValueEur = 0;
  let boundValueEur = 0;
  const rawEvents = [];

  for (const sym of coins || []) {
    const lots = buildTaxLots(sym, dustFreeTxs);
    if (!lots.length) continue;
    const coinId = COIN_MAP[sym]?.id || null;
    const price = coinId ? marketData?.[coinId]?.price : null;
    const hasPrice = typeof price === 'number' && price > 0;

    for (const lot of lots) {
      const valueEur = hasPrice ? lot.amount * price : null;
      if (isLotFree(lot.buyDate, today)) {
        // Bereits außerhalb der Frist (INV6: nie steuerpflichtig) → kein Ereignis,
        // fließt nur in den „frei"-Wert.
        if (hasPrice) freeValueEur += valueEur;
      } else {
        if (hasPrice) boundValueEur += valueEur;
        rawEvents.push({
          sym, coinId,
          amount: lot.amount,
          buyDate: lot.buyDate,
          freeDate: lotFreeDate(lot.buyDate),
          freeMs: lotFreeMs(lot.buyDate),
          days: daysUntilFree(lot.buyDate, today),
          valueEur,
          hasPrice,
        });
      }
    }
  }

  // Verdichtung je (Coin · Frei-Datum) — analog mergeTxsByDay (arch_tx_consolidation):
  // mehrere am selben Tag fällig werdende Lots desselben Coins = EIN Listeneintrag.
  const merged = new Map();
  for (const ev of rawEvents) {
    if (ev.freeMs == null) continue; // ungültiges Datum (defensiv) — nicht listen
    const key = `${ev.sym}|${ev.freeDate}`;
    const g = merged.get(key);
    if (!g) {
      merged.set(key, { ...ev });
    } else {
      g.amount += ev.amount;
      // Innerhalb einer Gruppe ist hasPrice konstant (gleicher Coin → gleicher Preis).
      if (g.hasPrice && ev.hasPrice) g.valueEur += ev.valueEur;
    }
  }

  const events = [...merged.values()].sort(
    (a, b) => (a.freeMs - b.freeMs) || (a.sym < b.sym ? -1 : a.sym > b.sym ? 1 : 0)
  );

  const totalValueEur = freeValueEur + boundValueEur;
  const hasPricedHoldings = totalValueEur > 0;
  const freePct = hasPricedHoldings ? (freeValueEur / totalValueEur) * 100 : null;

  return {
    freeValueEur, boundValueEur, totalValueEur,
    freePct, hasPricedHoldings,
    events,
    nextEvent: events[0] || null,
    eventCount: events.length,
  };
}
